/**
 * SuperAdminLogin.jsx
 *
 * Authentication for the SeaSuite super-admin portal.
 *
 * Auth flow:
 *   1. User clicks "Sign in with Google" (Google Identity Services)
 *   2. Token verified server-side by superadmin-google-login Azure Function
 *   3. Email checked against superadmin_users table
 *   4. No factor enrolled: JWT issued and stored — user is logged in.
 *      Factor enrolled: no JWT yet — the authenticator code or passkey is
 *      proved at superadmin-login-verify first (FDNA-02b-2), then the JWT.
 *   Fallback: email/password login via superadmin-login Azure Function
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getDomainBranding } from '../../tenant/tenantConfig.js';
import { setSessionToken } from '../../api/azureClient.js';
import { GOOGLE_CLIENT_ID, useGoogleSignInButton } from './useGoogleSignInButton.js';
import { callSuperAdminLoginVerify } from '../../api/mfaApi.js';
import MfaVerifyStep from '../auth/MfaVerifyStep.jsx';

const AZURE_FUNCTIONS_URL = import.meta.env.VITE_AZURE_FUNCTIONS_URL;

const _ALLOWED_EMAIL = import.meta.env.VITE_SUPER_ADMIN_EMAIL || '';

// Decode a JWT payload (without verification) to check expiry client-side.
function isJwtExpired(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.exp ? Date.now() / 1000 > decoded.exp : false;
  } catch {
    return true; // Treat unparseable tokens as expired
  }
}

// Store the super-admin session and hand it to the portal.
function completeSession(token, user, onLogin) {
  localStorage.setItem('seasuite_token_superadmin', token);
  localStorage.setItem(
    'seasuite_session_superadmin',
    JSON.stringify({
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      role: 'super-admin',
    })
  );
  setSessionToken(token);
  onLogin({
    email: user.email,
    id: user.id,
    role: 'superadmin',
    displayName: user.displayName,
  });
}

/**
 * superadmin-login-verify behind MfaVerifyStep's action names. The challenge
 * token from the first step rides along on every call.
 */
function loginVerifyCall(challengeToken) {
  return (action, body) => {
    if (action === 'webauthn-options')
      return callSuperAdminLoginVerify('begin', { challengeToken });
    if (action === 'webauthn-verify')
      return callSuperAdminLoginVerify('verify', { ...body, challengeToken });
    return callSuperAdminLoginVerify('verify', { challengeToken, code: body.code });
  };
}

// ── Tiny UI helpers ───────────────────────────────────────────

function Card({ children }) {
  return (
    <div className="card" style={{ padding: 32, width: '100%', maxWidth: 420 }}>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--fg3)',
        fontFamily: 'var(--font-mono)',
        marginBottom: 6,
      }}
    >
      {children}
    </label>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 8,
        background: 'var(--danger-bg)',
        border: '1px solid rgba(251,113,133,0.25)',
        color: 'var(--danger)',
        fontSize: 12,
        marginBottom: 16,
      }}
    >
      {msg}
    </div>
  );
}

function PrimaryBtn({ onClick, disabled, children }) {
  return (
    <button
      className="btn"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        justifyContent: 'center',
        padding: '11px 0',
        fontSize: 13,
        background: 'var(--warn)',
        color: '#1a0a00',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: 10,
        fontWeight: 600,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Logo() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 40 }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: 'linear-gradient(135deg, var(--warn), #fb923c)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 800,
          color: '#1a0a00',
          fontFamily: 'var(--font-display)',
          margin: '0 auto 16px',
        }}
      >
        {getDomainBranding().initial}
      </div>
      <div className="display" style={{ fontSize: 24, fontWeight: 600, color: 'var(--fg)' }}>
        {getDomainBranding().name}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--warn)',
          marginTop: 4,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
        }}
      >
        SUPER ADMIN PORTAL
      </div>
    </div>
  );
}

// ── Access denied ─────────────────────────────────────────────

function _AccessDeniedStep({ email, onSignOut }) {
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🚫</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)', marginBottom: 8 }}>
          Access denied
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
          <strong style={{ color: 'var(--fg)' }}>{email}</strong> is not authorised to access this
          portal.
        </div>
        <PrimaryBtn onClick={onSignOut}>Sign out</PrimaryBtn>
      </div>
    </Card>
  );
}

// ── Azure Google SSO login step ──────────────────────────────

function AzureGoogleLoginStep({ onLogin, onError, onChallenge }) {
  const [loading, setLoading] = useState(false);
  const googleBtnRef = useRef(null);

  const handleGoogleCredential = useCallback(
    async (response) => {
      setLoading(true);
      onError('');
      try {
        const res = await fetch(`${AZURE_FUNCTIONS_URL}/api/superadmin-google-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: response.credential }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          onError(data.error || 'Login failed');
          return;
        }

        const data = await res.json();
        if (data.mfaRequired) {
          onChallenge(data);
          return;
        }
        completeSession(data.token, data.user, onLogin);
      } catch (_e) {
        onError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [onLogin, onError, onChallenge]
  );

  const gsiReady = useGoogleSignInButton(googleBtnRef, handleGoogleCredential);

  // If no Google Client ID configured, fall back to password login
  if (!GOOGLE_CLIENT_ID) {
    return <AzurePasswordLoginStep onLogin={onLogin} onError={onError} onChallenge={onChallenge} />;
  }

  return (
    <Card>
      <div style={{ marginBottom: 24, fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
        Administrator sign in
      </div>
      <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 24, lineHeight: 1.6 }}>
        Access is restricted to authorised accounts only. Sign in with your Google account to
        continue.
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: 'var(--fg3)' }}>
          Signing in…
        </div>
      )}
      {!gsiReady && !loading && (
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--fg3)', padding: '12px 0' }}>
          Loading…
        </div>
      )}
      <div
        ref={googleBtnRef}
        style={{
          display: loading || !gsiReady ? 'none' : 'flex',
          justifyContent: 'center',
          minHeight: 44,
        }}
      />
    </Card>
  );
}

// ── Azure password login step (fallback) ─────────────────────

function AzurePasswordLoginStep({ onLogin, onError, onChallenge }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    onError('');

    try {
      const res = await fetch(`${AZURE_FUNCTIONS_URL}/api/superadmin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onError(data.error || 'Login failed');
        return;
      }

      const data = await res.json();
      if (data.mfaRequired) {
        onChallenge(data);
        return;
      }
      completeSession(data.token, data.user, onLogin);
    } catch (_e) {
      onError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div style={{ marginBottom: 24, fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
        Administrator sign in
      </div>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <Label>Email</Label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            style={{
              width: '100%',
              padding: '9px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--bg2)',
              color: 'var(--fg)',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <Label>Password</Label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={{
              width: '100%',
              padding: '9px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--bg2)',
              color: 'var(--fg)',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <PrimaryBtn disabled={loading || !email || !password}>
          {loading ? 'Signing in…' : 'Sign in'}
        </PrimaryBtn>
      </form>
    </Card>
  );
}

// ── Root component ────────────────────────────────────────────

export default function SuperAdminLogin({ onLogin }) {
  const [error, setError] = useState('');
  // { method, challengeToken } once the first factor passed on an enrolled account
  const [factor, setFactor] = useState(null);

  useEffect(() => {
    // Restore Azure super-admin session from localStorage
    try {
      const storedSession = localStorage.getItem('seasuite_session_superadmin');
      const storedToken = localStorage.getItem('seasuite_token_superadmin');
      if (storedSession && storedToken) {
        if (isJwtExpired(storedToken)) {
          localStorage.removeItem('seasuite_token_superadmin');
          localStorage.removeItem('seasuite_session_superadmin');
        } else {
          const sess = JSON.parse(storedSession);
          setSessionToken(storedToken);
          onLogin({
            email: sess.email,
            id: sess.id,
            role: 'superadmin',
            displayName: sess.displayName,
          });
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const _handleSignOut = async () => {
    localStorage.removeItem('seasuite_token_superadmin');
    localStorage.removeItem('seasuite_session_superadmin');

    setSessionToken(null);
    setError('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        fontFamily: 'var(--font-body)',
        padding: '0 24px',
      }}
    >
      <Logo />
      <ErrorBox msg={error} />
      {factor ? (
        <Card>
          <MfaVerifyStep
            method={factor.method}
            challengeToken={factor.challengeToken}
            callVerify={loginVerifyCall(factor.challengeToken)}
            onSuccess={(token, user) => completeSession(token, user, onLogin)}
            onBack={() => {
              setFactor(null);
              setError('');
            }}
          />
        </Card>
      ) : (
        <AzureGoogleLoginStep
          onLogin={onLogin}
          onError={setError}
          onChallenge={(data) => {
            setError('');
            setFactor({ method: data.method, challengeToken: data.challengeToken });
          }}
        />
      )}
    </div>
  );
}
